import React, { FC } from 'react';
import { IoSearch } from 'react-icons/io5';
import { TbX } from 'react-icons/tb';

import { ActionIcon, ActionIconProps, Box, Button, Group, Loader, Text, Textarea, TextareaProps } from '@mantine/core';

import { useIsMobile } from 'hooks/use-is-mobile';

import cx from './SearchBox.module.scss';

interface Props {
    isLoading?: boolean;
    showSubmitHint?: boolean;
    submit?: () => void;
    handleReset?: () => void;
    rightSection?: React.ReactNode;
    autocompleteOpen?: boolean;
    showSubmit?: boolean;
}

const SearchBox: FC<Props & TextareaProps> = ({
    isLoading,
    showSubmitHint,
    submit,
    handleReset,
    leftSection,
    rightSection = null,
    autocompleteOpen,
    showSubmit,
    ...props
}) => {
    const isMobile = useIsMobile('sm');

    const renderLeftSection = () => {
        if (isLoading) {
            return <Loader size={20} />;
        }

        if (leftSection) {
            return leftSection;
        }

        return <IoSearch size={20} />;
    };

    const renderRightSection = () => {
        const children = [];

        if (rightSection) {
            children.push(<Box key="right-section">{rightSection}</Box>);
        }

        if (showSubmitHint) {
            children.push(
                <Text fz="sm" c="gray.5" key="shortcut-hint">
                    ↵
                </Text>,
            );
        }

        if (handleReset) {
            children.push(
                <ResetButton
                    size={props.size === 'md' ? 'compact-sm' : 'sm'}
                    handleClick={() => {
                        handleReset();
                    }}
                    key="reset"
                />,
            );
        }

        if (submit && showSubmit) {
            if (isMobile) {
                children.push(
                    <Button
                        className={cx.submit}
                        size="compact-md"
                        style={{
                            height: 32,
                            fontSize: 10,
                            right: 2,
                        }}
                        onClick={submit}
                        key="submit-button-mobile"
                    >
                        Search
                    </Button>,
                );
            } else {
                children.push(
                    <Button
                        className={cx.submit}
                        size={props.size === 'md' ? 'compact-md' : 'lg'}
                        onClick={submit}
                        key="submit-button-desktop"
                    >
                        Search
                    </Button>,
                );
            }
        }

        return children;
    };

    return (
        <Textarea
            autosize
            maxRows={props.value ? undefined : 1}
            classNames={cx}
            enterKeyHint="search"
            leftSection={renderLeftSection()}
            rightSection={
                <Group gap={8} wrap="nowrap">
                    {renderRightSection()}
                </Group>
            }
            rightSectionWidth="fit-content"
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    submit?.();
                }
            }}
            data-with-submit={showSubmit}
            data-autocomplete-open={autocompleteOpen}
            {...props}
            data-search-box
        />
    );
};

const ResetButton: FC<{
    size?: ActionIconProps['size'];
    handleClick: () => void;
}> = ({ size, handleClick }) => {
    return (
        <ActionIcon onClick={handleClick} className={cx.reset} variant="white" size={size} mr={8}>
            <TbX size={16} />
        </ActionIcon>
    );
};

export { SearchBox };
